// src/App.jsx

import { useEffect } from 'react';
import AppRouter, { isFormsSubdomain } from './routes/index.jsx';
import { useCancelRequestPolling } from './hooks/useCancelRequestPolling';
import { useUser } from './contexts/UserContext';
import { apiService } from './services/api';
import { isOfflineModeEnabled, rememberUser, prefetchService } from './services/offline';
import { useVeradigmStore } from './stores/veradigmStore';
import { useGoogleCalendarStore } from './stores/googleCalendarStore';
import { useVeradigmPMStore } from './stores/veradigmPMStore';
import { FeatureProvider, useFeatureFlags } from './contexts/FeatureContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { UpdatePrompt } from './components/UpdatePrompt';
import { ContentProtection } from './components/security/ContentProtection';

function AppContent() {
  const { user, isAuthenticated } = useUser();
  const { solutionDetails, solutionsLoaded } = useFeatureFlags();
  const { setConnectionStatus, setFeatureEnabled, setLoading } = useVeradigmStore();
  const { setOrgEnabled, setConnectionStatus: setGCalConnectionStatus, setLoading: setGCalLoading } = useGoogleCalendarStore();
  const { setUsesVeradigmBilling, setPMConfig, setUserPMCredentials } = useVeradigmPMStore();

  // Derive Veradigm integration status from FeatureContext (no extra API call)
  useEffect(() => {
    if (!solutionsLoaded || !isAuthenticated || !user?.id) return;

    const solutionData = solutionDetails.erx_mobile;
    const ehrIntegration = solutionData?.settings?.ehr_integration || {};
    const isFeatureEnabled = ehrIntegration.enabled && ehrIntegration.provider === 'veradigm';

    setFeatureEnabled(isFeatureEnabled);

    if (isFeatureEnabled) {
      setLoading(true);
      apiService.checkVeradigmConnection(user.id)
        .then(res => setConnectionStatus(res.success || false))
        .catch(err => {
          console.error('Error checking Veradigm connection:', err);
          setConnectionStatus(false);
        })
        .finally(() => setLoading(false));
    } else {
      setConnectionStatus(false);
    }
  }, [solutionsLoaded, solutionDetails, isAuthenticated, user?.id, setFeatureEnabled, setConnectionStatus, setLoading]);

  // Check Google Calendar integration status on app startup
  useEffect(() => {
    const checkGoogleCalendarIntegration = async () => {
      if (!isAuthenticated || !user?.id || !user?.organization?.id) return;

      const enabled = user.organization.settings?.google_calendar_enabled === true;
      setOrgEnabled(enabled);

      if (enabled) {
        setGCalLoading(true);
        try {
          const status = await apiService.getGoogleCalendarStatus();
          setGCalConnectionStatus(status);
        } catch (error) {
          console.error('Error checking Google Calendar status:', error);
          setGCalConnectionStatus(null);
        } finally {
          setGCalLoading(false);
        }
      }
    };

    checkGoogleCalendarIntegration();
  }, [user?.id, user?.organization?.id, isAuthenticated, setOrgEnabled, setGCalConnectionStatus, setGCalLoading]);

  // Check Veradigm PM billing integration on app startup
  useEffect(() => {
    const checkPMIntegration = async () => {
      if (!isAuthenticated || !user?.organization) return;

      const usesVeradigmBilling = user.organization.settings?.uses_veradigm_billing === true;
      setUsesVeradigmBilling(usesVeradigmBilling);

      if (!usesVeradigmBilling) return;

      try {
        const config = await apiService.getVeradigmPMConfig();
        setPMConfig(config);

        const userConfig = await apiService.getVeradigmPMUserConfig();
        setUserPMCredentials(!!userConfig);
      } catch (err) {
        setPMConfig(null);
      }
    };

    checkPMIntegration();
  }, [user?.organization?.id, isAuthenticated, setUsesVeradigmBilling, setPMConfig, setUserPMCredentials]);

  // Offline mode: set user for feature flag, persist the boot snapshot, and
  // trigger background prefetch
  useEffect(() => {
    if (!isAuthenticated || !user?.id || !user?.organization?.id) return;
    rememberUser(user);

    if (isOfflineModeEnabled()) {
      prefetchService.prefetchForProvider(user.id, user.organization.id, user.id, apiService);
    }
  }, [isAuthenticated, user?.id, user?.organization?.id]);

  // polling request for cancellation responses
  useCancelRequestPolling(120000);

  return (
    <>
      <AppRouter />
      {/* Patients filling forms on the forms subdomain must never be interrupted by the update banner */}
      {!isFormsSubdomain() && <UpdatePrompt />}
      {!isFormsSubdomain() && <ContentProtection />}
    </>
  );
}

export default function App() {
  return (
    <FeatureProvider>
      <PermissionProvider>
        <AppContent />
      </PermissionProvider>
    </FeatureProvider>
  );
}